export const TOTAL_SLIDES = 12

export const attentionDemands = [
  'Read more',
  'Watch more',
  'Learn more',
  'Book more time',
]

export const rewards = [
  'Time saved',
  'Workflow clarity',
  'Clinical insight',
  'Less friction',
  'A reason to go deeper',
]

export const recognitionThoughts = [
  'This will be quick.',
  'This will be useful.',
  'Solventum usually delivers.',
]

/* ── Page 5 · Same contract. Different reward. ───────────────────────── */

export type ProofPoint = {
  product: string
  invested: string
  statement: string
  statementScale: 'xl' | 'md'
  reward: string
  support: string
  bonus?: string
}

export const proofPoints: ProofPoint[] = [
  {
    product: 'Digital Bonding',
    invested: '18 seconds invested →',
    statement: '18 minutes back',
    statementScale: 'xl',
    reward: 'Reward: Time',
    support: 'A faster bonding workflow.',
  },
  {
    product: 'Precision Grip Attachments',
    invested: '30 seconds invested →',
    statement: 'Protect more of the precision you planned',
    statementScale: 'md',
    reward: 'Reward: Precision',
    support:
      'Reduce manual variability between the digital plan and the bonded attachment.',
    bonus: '+ 12 minutes saved per bonding procedure, on average',
  },
]

/* ── Page 6 · The operating model ────────────────────────────────────── */

export const operatingSteps = [
  { label: 'Choose', detail: 'Priority products' },
  { label: 'Find', detail: 'The HCP problem' },
  { label: 'Define', detail: 'The reward' },
  { label: 'Create', detail: 'The campaign' },
  { label: 'Run', detail: 'Across channels' },
  { label: 'Learn', detail: 'From response' },
  { label: 'Repeat', detail: '' },
]

/* ── Page 7 · The portfolio ──────────────────────────────────────────── */

export type PortfolioRow = {
  product: string
  problem: string
  difference: string
  reward: string
  defined: boolean
}

export const portfolioRows: PortfolioRow[] = [
  {
    product: 'Digital Bonding',
    problem: 'Bonding workflow takes time',
    difference: 'Digital workflow',
    reward: 'Time',
    defined: true,
  },
  {
    product: 'Precision Grip',
    problem: 'Precision can be lost between digital planning and bonding',
    difference: 'Digitally planned, custom manufactured attachment',
    reward: 'Precision',
    defined: true,
  },
  {
    product: 'Clarity Portal',
    problem: 'To define',
    difference: 'To define',
    reward: 'To define',
    defined: false,
  },
  {
    product: 'Product 4',
    problem: 'To define',
    difference: 'To define',
    reward: 'To define',
    defined: false,
  },
]

export const portfolioMethod = ['Problem', 'Difference', 'Reward', 'Promise']

/* ── Page 8 · The story method ───────────────────────────────────────── */

export const beliefSteps = [
  {
    order: '01',
    label: 'What they already know',
    text: 'Attachment precision matters.',
  },
  {
    order: '02',
    label: 'What they have accepted',
    text: 'Some manual variability is part of the workflow.',
  },
  {
    order: '03',
    label: 'What has changed',
    text: 'The attachment itself can now become part of the digital workflow.',
  },
  {
    order: '04',
    label: 'The updated conclusion',
    text: 'Precision does not have to stop at the digital plan.',
  },
]

/* ── Page 9 · The rhythm ─────────────────────────────────────────────── */

export const calendarMonths = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']

export type CalendarCell = { label: string; opening?: boolean } | null

export type CalendarRow = {
  product: string
  cells: CalendarCell[]
}

export const calendarRows: CalendarRow[] = [
  {
    product: 'Digital Bonding',
    cells: [
      { label: '18 sec → Time', opening: true },
      null,
      { label: 'Workflow' },
      null,
      { label: 'Delegation' },
      null,
    ],
  },
  {
    product: 'Precision Grip',
    cells: [
      null,
      { label: '30 sec → Precision', opening: true },
      null,
      { label: 'Tracking' },
      null,
      { label: 'Flash' },
    ],
  },
  {
    product: 'Clarity Portal',
    cells: [
      null,
      null,
      { label: '30 sec → TBD', opening: true },
      null,
      null,
      { label: 'Workflow' },
    ],
  },
  {
    product: 'Product 4',
    cells: [null, null, null, null, { label: 'Launch' }, null],
  },
]

export const campaignArcs = [
  {
    product: 'Precision Grip',
    arc: ['Precision', 'Tracking', 'Flash', 'Workflow'],
  },
  {
    product: 'Digital Bonding',
    arc: ['Time', 'Workflow', 'Delegation', 'Throughput'],
  },
]

/* ── Page 10 · The ecosystem ─────────────────────────────────────────── */

export const touchpoints = [
  'Email',
  'Banners',
  'Congress',
  'Sales rep',
  'QR',
  'Social',
  'Clarity Portal',
  'Microsite',
]

/* ── Page 11 · The start ─────────────────────────────────────────────── */

export const solventumInputs = [
  { text: 'Prioritise 3–5 products' },
  { text: 'Define the clinical / commercial priorities' },
  { text: 'Provide the evidence and claims' },
  {
    text: 'Identify key campaign moments',
    note: 'Congresses, launches, sales priorities, seasonal activity',
  },
  {
    text: 'Agree what success should change',
    note: 'Awareness, belief, engagement, action',
  },
]

export const snakkerInputs = [
  { text: 'Find the strongest HCP problem' },
  { text: 'Define the value exchange' },
  { text: 'Develop the campaign stories' },
  { text: 'Build the Snaks' },
  { text: 'Adapt them across markets and channels' },
  { text: 'Measure quantitative + qualitative response' },
  { text: 'Use the results to improve the next campaign' },
]

/* ── Page 12 · The habit ─────────────────────────────────────────────── */

export const trustSteps = [
  {
    order: 'First time',
    quote: '“What’s this?”',
    text: 'HCP sees the countdown.',
  },
  {
    order: 'Second time',
    quote: '“I’ve seen this before.”',
    text: 'HCP recognizes the format.',
  },
  {
    order: 'Over time',
    quote: '“Solventum usually makes this worth my time.”',
    text: 'HCP expects the payback.',
  },
]

export const trustOutcomes = [
  'Higher engagement',
  'Greater willingness to click',
  'Stronger credibility',
  'More trust in future communication',
]

export const closingActions = [
  'Choose the products.',
  'Define the rewards.',
  'Build the calendar.',
  'Earn the habit.',
]
